import * as uuid from 'uuid';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RequestRoot, SearchOption } from 'types';
import { MobileStorageManager } from './mobile-storage-manager';
import { RequestSummaryFeatures } from '../model/request-summary-features';
import { RequestDataAccessService } from 'src/webid/4.0/projects/data-access/src/lib/request-data-access.service';

@Injectable()
export class MobileRequestDataAccessService implements RequestDataAccessService {
    constructor(private storageManager: MobileStorageManager) {}

    createUID(): Observable<string> {
        return this.asObservable(() => {
            return 'UNDEFINED';
        });
    }

    count(filter: Object): Observable<number> {
        var countFilter = this.toCountingFilter(filter);
        return this.find(countFilter).pipe(map((list) => list.length));
    }

    find(query: Object): Observable<RequestRoot[]> {
        return this.asObservable(() => {
            let list = this.storageManager.loadRequestSummaryListFromStorage();
            let filtered = this.filterRequestList(list, (query as any)['selectors']);
            return this.limitRequestList(filtered, (query as any)['limits']);
        });
    }

    load(request: RequestRoot): Observable<RequestRoot> {
        const requestId = request.id ?? '';
        return this.asObservable(() => {
            return this.storageManager.loadRequestDataFromStorage(requestId);
        });
    }

    save(request: RequestRoot): Observable<void> {
        return this.asObservable(() => {
            if (request.id) {
                throw 'Cannot INSERT';
            }

            request.id = uuid.v4().toString();
            let summaryItem = this.createRequestSummary(request);
            this.storageManager.storeSummaryItemToStorage(summaryItem);
            this.storageManager.storeRequestDataToStorage(request);
        });
    }

    edit(request: RequestRoot): Observable<void> {
        return this.asObservable(() => {
            if (!request.id) {
                throw 'Cannot UPDATE';
            }
            let summaryItem = this.createRequestSummary(request);
            this.storageManager.storeSummaryItemToStorage(summaryItem);
            this.storageManager.storeRequestDataToStorage(request);
        });
    }

    wipe(request: RequestRoot): Observable<void> {
        return this.asObservable(() => {
            this.storageManager.removeRequestSummaryItemFromStorage(request.id);
            this.storageManager.removeRequestDataFromStorage(request.id);
        });
    }

    private filterRequestList(list: RequestRoot[], selectors: any): RequestRoot[] {
        if (!selectors || Object.keys(selectors).length == 0) {
            return list;
        }
        return list.filter((request) => {
            for (let key in selectors) {
                if (Object.prototype.hasOwnProperty.call(selectors, key)) {
                    let selectorValue: string = (selectors as any)[key].val;
                    let selectorOption: SearchOption = (selectors as any)[key].opt;
                    if (selectorOption.name === SearchOption.Partial.name) {
                        let featureValue: string | null = this.getValueByPath<string>(request, key);
                        if (featureValue == null || featureValue.toUpperCase().indexOf(selectorValue.toUpperCase()) == -1) {
                            return false;
                        }
                    }
                }
            }
            return true;
        });
    }

    private limitRequestList(list: RequestRoot[], limits: any): RequestRoot[] {
        if (!limits || (limits.offset === 0 && limits.count == -1)) {
            return list;
        }
        return list.slice(limits.offset, limits.offset + limits.count);
    }

    private getValueByPath<T>(obj: Object, path: string): T | null {
        let current = obj;
        let parts: string[] = path.split('.');
        for (let i = 0; i < parts.length; ++i) {
            current = (current as any)[parts[i]];
            if (current == null || current == undefined) {
                return null;
            }
        }
        return current as T;
    }

    private asObservable<T>(action: () => T): Observable<T> {
        return new Observable<T>((observer) => {
            try {
                observer.next(action());
                observer.complete();
            } catch (error: any) {
                observer.error(error);
            }
        });
    }

    private createRequestSummary(request: RequestRoot): RequestRoot {
        const summary = new RequestRoot();
        
        summary.id = request.id;
        summary.uid = request.uid;
        summary.status = request.status;
        summary.eventHistory = request.eventHistory;
        summary.revision = request.revision;
        summary.rawBlobs = [];
        summary.imgBlobs = [];
        summary.comments = request.comments;
        summary.features = { applicant: new RequestSummaryFeatures() };
        summary.features.applicant.personIdentity = request.features.applicant.personIdentity;
        summary.features.asString = request.features.asString;

        return summary;
    }

    private toCountingFilter(filter: any): any {
        return {
            selectors: (filter as any)['selectors'],
            limits: { offset: 0, count: -1 },
            orders: {},
        };
    }
}
